import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Megaphone } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdsPublic() {
  const [board, setBoard] = useState(null);
  const [market, setMarket] = useState("");

  useEffect(() => {
    api.get("/ads/public", { params: market ? { market } : {} })
      .then(({ data }) => setBoard(data))
      .catch(() => setBoard({ ads: [], markets: [] }));
  }, [market]);

  useSeo({
    title: "Tools and machines on offer | Unity & Utility",
    description: "What we are selling right now — tools, machines and site equipment, with prices for your market and where to buy.",
    path: "/ads",
    image: board?.picture ? `${BACKEND}${board.picture}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["On offer", "/ads"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="ads-public-page">
        <Crumbs items={[["Home", "/"], ["On offer", "/ads"]]} />
        <p className="overline text-[#B3241B]">On offer</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Megaphone size={36} className="mt-1 shrink-0 text-[#002FA7]" weight="duotone" />
          Tools and machines on offer
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700">
          Priced for your market, with the picture and wording redrawn every day. Each advert also
          shows where else you can buy the same kind of kit.
        </p>
        <div className="mt-5">
          <ShareBar url={`${BACKEND}/api/share/ads`} text="Tools and machines on offer"
            testid="ads-public-share" instagram />
        </div>

        {board?.markets?.length > 0 && (
          <div className="scroll-x mt-8 flex gap-1.5 overflow-x-auto" data-testid="ads-market-filter">
            {[["", "Everywhere"], ...board.markets.map((m) => [m.code, m.label])].map(([code, label]) => (
              <button key={code || "all"} data-testid={`ads-market-${code || "all"}`} onClick={() => setMarket(code)}
                className={`tap-safe shrink-0 border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                  market === code ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {board?.ads?.length === 0 ? (
          <p className="mt-8 text-sm text-slate-500" data-testid="ads-public-empty">
            Nothing on offer for that market just now.
          </p>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="ads-public-list">
            {board?.ads?.map((ad, i) => (
              <div key={ad.id} data-testid={`ads-public-${i}`} className="border border-border bg-white">
                {ad.image_url && (
                  <Link to={`/ads/${ad.token}`}>
                    <img src={`${BACKEND}${ad.image_url}`} alt={ad.product} className="h-44 w-full object-cover" />
                  </Link>
                )}
                <div className="p-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">{ad.market_label}</p>
                  <Link to={`/ads/${ad.token}`} className="mt-1 block font-semibold text-[#0F172A] hover:text-[#002FA7]">
                    {ad.headline}
                  </Link>
                  <p className="mt-1 text-sm font-bold text-[#0B5C2E]">{ad.symbol}{ad.price}</p>
                  <div className="mt-2">
                    <ShareBar url={`${BACKEND}/api/share/ad/${ad.token}`} text={ad.headline}
                      testid={`ads-public-share-${i}`} compact instagram />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicShell>
  );
}

export function AdPublic() {
  const { token } = useParams();
  const [ad, setAd] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/ads/public/${token}`)
      .then(({ data }) => setAd(data))
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "Not available"));
  }, [token]);

  useSeo({
    title: ad ? `${ad.headline} | Unity & Utility` : "On offer | Unity & Utility",
    description: (ad?.copy || "Tools and machines from a working industrial supplier.").slice(0, 180),
    path: `/ads/${token}`,
    image: ad?.image_url ? `${BACKEND}${ad.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["On offer", "/ads"], [ad?.product || "Advert", null]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="ad-public-page">
        <Crumbs items={[["Home", "/"], ["On offer", "/ads"], [ad?.product || "Advert", null]]} />
        {error && <p data-testid="ad-public-error" className="mt-6 text-sm text-slate-500">{error}</p>}
        {ad && (
          <>
            <p className="overline text-[#B3241B]">{ad.market_label} · {ad.currency}</p>
            <h1 data-testid="ad-public-title" className="font-display mt-2 text-3xl font-bold leading-[1.1] tracking-tight text-[#0F172A] sm:text-4xl">
              {ad.headline}
            </h1>
            {ad.image_url && (
              <img data-testid="ad-public-image" src={`${BACKEND}${ad.image_url}`} alt={ad.product}
                className="mt-6 w-full border border-border object-cover" />
            )}
            <p data-testid="ad-public-price" className="mt-5 text-2xl font-bold text-[#0B5C2E]">{ad.symbol}{ad.price}</p>
            {ad.variations.map((v) => (
              <p key={v.angle} className="mt-3 whitespace-pre-line text-base leading-relaxed text-slate-700">{v.text}</p>
            ))}
            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Share it</p>
              <div className="mt-2">
                <ShareBar url={`${BACKEND}/api/share/ad/${token}`} text={ad.headline}
                  testid="ad-public-share" instagram />
              </div>
            </div>
            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Also stocked at</p>
              <div className="scroll-x mt-2 flex gap-2 overflow-x-auto" data-testid="ad-public-links">
                {ad.links.map((l) => (
                  <a key={l.key} href={l.url} target="_blank" rel="noreferrer"
                    data-testid={`ad-public-link-${l.key}`}
                    className="tap-safe shrink-0 border border-border px-2.5 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
                    {l.label}
                  </a>
                ))}
              </div>
            </div>
            <Link to="/ads" data-testid="ad-public-back"
              className="mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
              See everything on offer
            </Link>
          </>
        )}
      </div>
    </PublicShell>
  );
}
